import { ActionModal } from '@kcftech/design-system';
import {
    ICorporationResponse,
    ILocationResponse,
    MachineHealthEventType,
    NotificationHardwareType,
    NotificationType,
} from '@kcftech/prometheustypescriptapiclient';
import { User } from 'components/dropdown/UserDropdown';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useCreateNotificationSubscriptions } from 'custom_hooks/data_hooks/notification_subscriptions/useCreateNotificationSubscriptions';
import { CreateNotificationSubscriptionFormContent } from 'pages/Settings/NotificationSubscriptions/form/CreateNotificationSubscriptionFormContent';
import { validateSubscriptionForm } from 'pages/Settings/NotificationSubscriptions/form/shared';
import {
    CreateNotificationSubscriptionFormData,
    SupportedNotificationType,
} from 'pages/Settings/NotificationSubscriptions/form/types';
import {
    formattedNotificationTypes,
    NotificationEventCategory,
} from 'pages/Settings/NotificationSubscriptions/shared';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { uniqueBy } from 'utils/array';

export type CreateNotificationSubscriptionModalProps = Readonly<{
    corporation?: ICorporationResponse;
    location?: ILocationResponse;
    user?: User;
    handleClose: () => void;
    onCreate: () => void;
    defaultNotificationType: SupportedNotificationType | undefined;
}>;

export function CreateNotificationSubscriptionModal({
    corporation,
    location,
    user,
    handleClose,
    onCreate,
    defaultNotificationType = NotificationType.Indicator,
}: CreateNotificationSubscriptionModalProps) {
    const { showToast } = useNotificationContext();

    const {
        mutate: createNotificationSubscriptions,
        isPaused: isCreating,
    } = useCreateNotificationSubscriptions({
        onSuccess: (_data, formData) => {
            handleClose();
            onCreate();
            showToast({
                type: 'success',
                content: `Successfully created ${
                    formattedNotificationTypes[formData.type]
                } subscription(s)`,
            });
        },
        onError: (_err, formData) =>
            showToast({
                type: 'error',
                content: `Failed to create ${
                    formattedNotificationTypes[formData.type]
                } subscription(s)`,
            }),
    });

    const form = useForm<CreateNotificationSubscriptionFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldUnregister: false,
        resolver: validateSubscriptionForm,
        defaultValues: {
            type: defaultNotificationType,
            location,
            users: user !== undefined ? [user] : [],
            isSilenced: false,
            indicatorAlerts: {
                groupId: undefined,
                unacknowledgedPeriodMs: 0,
                notificationTriggers: {
                    [NotificationEventCategory.Alarm]: {
                        ActivityEventCategory: NotificationEventCategory.Alarm,
                        IsEmailEnabled: true,
                        IsSmsEnabled: true,
                    },
                    [NotificationEventCategory.Warning]: {
                        ActivityEventCategory: NotificationEventCategory.Warning,
                        IsEmailEnabled: true,
                        IsSmsEnabled: true,
                    },
                    [NotificationEventCategory.CriticalAlarmAll]: {
                        ActivityEventCategory: NotificationEventCategory.CriticalAlarmAll,
                        IsEmailEnabled: true,
                        IsSmsEnabled: true,
                    },
                    [NotificationEventCategory.CriticalAlarmTemperature]: {
                        ActivityEventCategory:
                            NotificationEventCategory.CriticalAlarmTemperature,
                        IsEmailEnabled: true,
                        IsSmsEnabled: true,
                    },
                },
            },
            hardware: {
                notificationTriggers: {
                    [NotificationHardwareType.BaseStation]: {
                        HardwareType: NotificationHardwareType.BaseStation,
                        IsEmailEnabled: true,
                        // TODO: default to true when available
                        IsSmsEnabled: false,
                    },
                    [NotificationHardwareType.Repeater]: {
                        HardwareType: NotificationHardwareType.Repeater,
                        IsEmailEnabled: true,
                        // TODO: default to true when available
                        IsSmsEnabled: false,
                    },
                },
            },
            machineHealth: {
                notificationTriggers: {
                    [MachineHealthEventType.IssueCreate]: {
                        IsEmailEnabled: true,
                        IsSdConnectPushEnabled: true,
                    },
                    // Including this here technically fixes #66155. Double
                    // check implicit "false" default values don't 400 on send
                    // (not included in the request) if you take this out
                    [MachineHealthEventType.DraftIssueCreate]: {
                        IsEmailEnabled: false,
                        IsSdConnectPushEnabled: false,
                    },
                    [MachineHealthEventType.IssueActivity]: {
                        IsEmailEnabled: false,
                        IsSdConnectPushEnabled: false,
                    },
                },
            },
            hardwareIssues: {
                faultTypes: [],
                allowInternalOnly: false,
            },
        },
    });

    const {
        formState: { isSubmitted, isValid, isValidating, isSubmitting },
        handleSubmit,
        setValue,
        getValues,
    } = form;

    const submit = handleSubmit((formData) => {
        if (!formData.indicatorAlerts) {
            throw new Error('Missing indicator alerts');
        }
        createNotificationSubscriptions(formData);
    });

    useEffect(() => {
        if (user) {
            setValue(
                'users',
                uniqueBy([...getValues('users'), user], (u) => u.Id)
            );
        }
    }, [user, setValue, getValues]);

    return (
        <ActionModal
            title="Create Notification Subscription"
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isCreating)
            }
            onAction={submit}
            onClose={handleClose}
            actionButtonText="Create"
            closeButtonText="Cancel"
            height={800}
        >
            <CreateNotificationSubscriptionFormContent
                form={form}
                corporation={corporation}
                location={location}
            />
        </ActionModal>
    );
}
